undefined/* =========================
 * Safety helpers
 * ========================= */
function safeUpperCode(event) {
  return ((event && event.code) || '').toUpperCase();
}

function safeClosest(el, selector) {
  try {
    return el && el.closest ? el.closest(selector) : null;
  } catch (e) {
    return null;
  }
}

function safeFocus(el) {
  try {
    el && el.focus && el.focus();
  } catch (e) {
    // ignore
  }
}

function safeSetAttr(el, name, value) {
  try {
    el && el.setAttribute && el.setAttribute(name, value);
  } catch (e) {
    // ignore
  }
}

function safeAddEventListener(target, type, handler, options) {
  try {
    target && target.addEventListener && target.addEventListener(type, handler, options);
  } catch (e) {
    console.warn('[safeAddEventListener] failed', type, e);
  }
}

function safeRemoveEventListener(target, type, handler, options) {
  try {
    target && target.removeEventListener && target.removeEventListener(type, handler, options);
  } catch (e) {
    console.warn('[safeRemoveEventListener] failed', type, e);
  }
}

/* =========================
 * Pub/Sub
 * ========================= */
function subscribe(eventName, callback) {
  if (!eventName || typeof callback !== 'function') {
    console.warn('[subscribe] invalid args', { eventName, callback });
    return () => {};
  }

  safeAddEventListener(document, eventName, callback);
  return () => safeRemoveEventListener(document, eventName, callback);
}

function publish(eventName, data) {
  if (!eventName) {
    console.warn('[publish] invalid eventName', eventName);
    return;
  }

  try {
    document.dispatchEvent(new CustomEvent(eventName, { detail: data }));
  } catch (e) {
    console.error('[publish] dispatchEvent failed', e);
  }
}

/* =========================
 * Focusables
 * ========================= */
function getFocusableElements(container) {
  if (!container || !container.querySelectorAll) return [];
  try {
    return Array.from(
      container.querySelectorAll(
        "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
      )
    );
  } catch (e) {
    console.warn('[getFocusableElements] failed', e);
    return [];
  }
}

/* =========================
 * Details/Summary init
 * ========================= */
function onKeyUpEscape(event) {
  if (safeUpperCode(event) !== 'ESCAPE') return;

  const openDetailsElement = safeClosest(event && event.target, 'details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector ? openDetailsElement.querySelector('summary') : null;
  try {
    openDetailsElement.removeAttribute('open');
  } catch (e) {
    // ignore
  }
  if (summaryElement) {
    safeSetAttr(summaryElement, 'aria-expanded', 'false');
    safeFocus(summaryElement);
  }
}

try {
  document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
    if (!summary) return;

    safeSetAttr(summary, 'role', 'button');
    safeSetAttr(summary, 'aria-expanded', summary.parentNode && summary.parentNode.hasAttribute
      ? String(summary.parentNode.hasAttribute('open'))
      : 'false'
    );

    const next = summary.nextElementSibling;
    if (next && next.id) {
      safeSetAttr(summary, 'aria-controls', next.id);
    }

    safeAddEventListener(summary, 'click', (event) => {
      const current = event && event.currentTarget;
      const details = safeClosest(current, 'details');
      if (!current || !details) return;
      safeSetAttr(current, 'aria-expanded', String(!details.hasAttribute('open')));
    });

    if (summary.closest && summary.closest('header-drawer, menu-drawer')) return;
    if (summary.parentElement) safeAddEventListener(summary.parentElement, 'keyup', onKeyUpEscape);
  });
} catch (e) {
  console.error('[Details init] failed', e);
}

/* =========================
 * Focus trap
 * ========================= */
window.trapFocusHandlers = window.trapFocusHandlers || {};

function trapFocus(container, elementToFocus = container) {
  if (!container) return;

  const elements = getFocusableElements(container);
  const first = elements[0] || container;
  const last = elements[elements.length - 1] || container;

  removeTrapFocus();

  window.trapFocusHandlers.focusin = (event) => {
    try {
      if (event.target !== container && event.target !== last && event.target !== first) return;
      safeAddEventListener(document, 'keydown', window.trapFocusHandlers.keydown);
    } catch (e) {
      console.warn('[trapFocus] focusin handler failed', e);
    }
  };

  window.trapFocusHandlers.focusout = function () {
    try {
      safeRemoveEventListener(document, 'keydown', window.trapFocusHandlers.keydown);
    } catch (e) {
      console.warn('[trapFocus] focusout handler failed', e);
    }
  };

  window.trapFocusHandlers.keydown = function (event) {
    try {
      if (safeUpperCode(event) !== 'TAB') return;

      if (event.target === last && !event.shiftKey) {
        event.preventDefault();
        safeFocus(first);
      }

      if ((event.target === container || event.target === first) && event.shiftKey) {
        event.preventDefault();
        safeFocus(last);
      }
    } catch (e) {
      console.warn('[trapFocus] keydown handler failed', e);
    }
  };

  safeAddEventListener(document, 'focusout', window.trapFocusHandlers.focusout);
  safeAddEventListener(document, 'focusin', window.trapFocusHandlers.focusin);

  if (elementToFocus && elementToFocus.focus) {
    safeFocus(elementToFocus);

    try {
      if (
        elementToFocus.tagName === 'INPUT' &&
        ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
        elementToFocus.value
      ) {
        elementToFocus.setSelectionRange(0, elementToFocus.value.length);
      }
    } catch (e) {
      // ignore selection errors
    }
  }
}

function removeTrapFocus(elementToFocus = null) {
  safeRemoveEventListener(document, 'focusin', window.trapFocusHandlers.focusin);
  safeRemoveEventListener(document, 'focusout', window.trapFocusHandlers.focusout);
  safeRemoveEventListener(document, 'keydown', window.trapFocusHandlers.keydown);

  if (elementToFocus && elementToFocus.focus) safeFocus(elementToFocus);
}

/* =========================
 * QuantityInput (safe)
 * ========================= */
class QuantityInput extends HTMLElement {
  constructor() {
    super();

    // console.log('QuantityInput constructor called'); // 本番ではオフ推奨

    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });

    if (this.input) {
      safeAddEventListener(this.input, 'change', this.onInputChange.bind(this));
    }

    this.querySelectorAll('button').forEach((button) => {
      safeAddEventListener(button, 'click', this.onButtonClick.bind(this));
      // console.log('Event listener added to button:', button.getAttribute('name'));
    });
  }

  onInputChange(event) {
    // console.log('Input changed:', event?.target?.value);
    this.validateQtyRules();
  }

  onButtonClick(event) {
    // console.log('Button clicked');
    event && event.preventDefault && event.preventDefault();
    event && event.stopPropagation && event.stopPropagation();

    if (!this.input) return;

    const previousValue = parseInt(this.input.value, 10);
    const prev = Number.isFinite(previousValue) ? previousValue : 1;

    const stepParsed = parseInt(this.input.step, 10);
    const step = Number.isFinite(stepParsed) ? stepParsed : 1;

    const minParsed = parseInt(this.input.min, 10);
    const min = Number.isFinite(minParsed) ? minParsed : 1;

    let max = 999999;
    const maxParsed = parseInt(this.input.max, 10);
    if (this.input.max && this.input.max !== 'null' && Number.isFinite(maxParsed)) {
      max = maxParsed;
    }

    const currentButton = event && event.currentTarget;
    const buttonName =
      (currentButton && currentButton.getAttribute && currentButton.getAttribute('name')) ||
      (event && event.target && event.target.getAttribute && event.target.getAttribute('name')) ||
      (event && event.target && event.target.closest && event.target.closest('button') && event.target.closest('button').getAttribute('name'));

    let newValue = prev;

    if (buttonName === 'plus') {
      newValue = Math.min(prev + step, max);
    } else if (buttonName === 'minus') {
      newValue = Math.max(prev - step, min);
    } else {
      // ボタン名が取れない/想定外でも落とさない
      return;
    }

    if (newValue !== prev) {
      this.input.value = String(newValue);
      try {
        this.input.dispatchEvent(this.changeEvent);
      } catch (e) {
        console.warn('[QuantityInput] dispatchEvent failed', e);
      }
      this.validateQtyRules();
      // console.log('Quantity updated:', prev, '->', newValue);
    }
  }

  validateQtyRules() {
    if (!this.input) return;

    const valueParsed = parseInt(this.input.value, 10);
    const value = Number.isFinite(valueParsed) ? valueParsed : 1;

    const minParsed = parseInt(this.input.min, 10);
    const min = Number.isFinite(minParsed) ? minParsed : 1;

    let max = 999999;
    const maxParsed = parseInt(this.input.max, 10);
    if (this.input.max && this.input.max !== 'null' && Number.isFinite(maxParsed)) {
      max = maxParsed;
    }

    const buttonMinus = this.querySelector(".quantity__button[name='minus']");
    if (buttonMinus && buttonMinus.classList) {
      buttonMinus.classList.toggle('disabled', value <= min);
    }

    const buttonPlus = this.querySelector(".quantity__button[name='plus']");
    if (buttonPlus && buttonPlus.classList) {
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }

  connectedCallback() {
    // console.log('QuantityInput connected');
    this.validateQtyRules();
  }
}

try {
  if (window.customElements && !customElements.get('quantity-input')) {
    customElements.define('quantity-input', QuantityInput);
  }
} catch (e) {
  console.error('[customElements] define quantity-input failed', e);
}

/* =========================
 * debounce / throttle (safe this)
 * ========================= */
function debounce(fn, wait) {
  let t;
  return function (...args) {
    clearTimeout(t);
    const ctx = this;
    t = setTimeout(() => {
      try {
        fn && fn.apply(ctx, args);
      } catch (e) {
        console.error('[debounce] callback failed', e);
      }
    }, wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = Date.now();
    if (now - lastCall < delay) return;
    lastCall = now;
    try {
      return fn && fn.apply(this, args);
    } catch (e) {
      console.error('[throttle] callback failed', e);
    }
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/* =========================
 * Shopify Common JS (guards)
 * ========================= */
if (typeof window.Shopify === 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    try {
      return fn.apply(scope, arguments);
    } catch (e) {
      console.error('[Shopify.bind] callback failed', e);
    }
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  try {
    if (!selector || !selector.options) return;
    for (var i = 0, count = selector.options.length; i < count; i++) {
      var option = selector.options[i];
      if (value == option.value || value == option.innerHTML) {
        selector.selectedIndex = i;
        return i;
      }
    }
  } catch (e) {
    console.warn('[Shopify.setSelectorByValue] failed', e);
  }
};

Shopify.addListener = function (target, eventName, callback) {
  try {
    if (!target) return;
    target.addEventListener
      ? target.addEventListener(eventName, callback, false)
      : target.attachEvent && target.attachEvent('on' + eventName, callback);
  } catch (e) {
    console.warn('[Shopify.addListener] failed', e);
  }
};

Shopify.postLink = function (path, options) {
  try {
    options = options || {};
    var method = options['method'] || 'post';
    var params = options['parameters'] || {};

    var form = document.createElement('form');
    form.setAttribute('method', method);
    form.setAttribute('action', path);

    for (var key in params) {
      var hiddenField = document.createElement('input');
      hiddenField.setAttribute('type', 'hidden');
      hiddenField.setAttribute('name', key);
      hiddenField.setAttribute('value', params[key]);
      form.appendChild(hiddenField);
    }
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  } catch (e) {
    console.error('[Shopify.postLink] failed', e);
  }
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  options = options || {};
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  if (!this.countryEl || !this.provinceEl || !this.provinceContainer) {
    console.warn('[CountryProvinceSelector] missing elements', {
      country_domid,
      province_domid,
      options,
    });
    return;
  }

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    try {
      var value = this.countryEl.getAttribute('data-default');
      Shopify.setSelectorByValue(this.countryEl, value);
      this.countryHandler();
    } catch (e) {
      console.warn('[CountryProvinceSelector.initCountry] failed', e);
    }
  },

  initProvince: function () {
    try {
      var value = this.provinceEl.getAttribute('data-default');
      if (value && this.provinceEl.options.length > 0) {
        Shopify.setSelectorByValue(this.provinceEl, value);
      }
    } catch (e) {
      console.warn('[CountryProvinceSelector.initProvince] failed', e);
    }
  },

  countryHandler: function () {
    try {
      var opt = this.countryEl.options[this.countryEl.selectedIndex];
      if (!opt) return;

      var raw = opt.getAttribute('data-provinces');
      var provinces = [];
      if (raw) {
        try {
          provinces = JSON.parse(raw) || [];
        } catch (e) {
          console.warn('[CountryProvinceSelector] JSON.parse(data-provinces) failed', e);
          provinces = [];
        }
      }

      this.clearOptions(this.provinceEl);
      if (provinces && provinces.length == 0) {
        this.provinceContainer.style.display = 'none';
      } else {
        for (var i = 0; i < provinces.length; i++) {
          var o = document.createElement('option');
          o.value = provinces[i][0];
          o.innerHTML = provinces[i][1];
          this.provinceEl.appendChild(o);
        }
        this.provinceContainer.style.display = '';
      }
    } catch (e) {
      console.warn('[CountryProvinceSelector.countryHandler] failed', e);
    }
  },

  clearOptions: function (selector) {
    try {
      while (selector && selector.firstChild) {
        selector.removeChild(selector.firstChild);
      }
    } catch (e) {
      console.warn('[CountryProvinceSelector.clearOptions] failed', e);
    }
  },

  setOptions: function (selector, values) {
    try {
      for (var i = 0; i < values.length; i++) {
        var opt = document.createElement('option');
        opt.value = values[i];
        opt.innerHTML = values[i];
        selector.appendChild(opt);
      }
    } catch (e) {
      console.warn('[CountryProvinceSelector.setOptions] failed', e);
    }
  },
};

/* =========================
 * MenuDrawer / HeaderDrawer / Modal / Media
 * - mostly original but guarded for nulls & key codes
 * ========================= */
class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    safeAddEventListener(this, 'keyup', this.onKeyUp.bind(this));
    safeAddEventListener(this, 'focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    try {
      this.querySelectorAll('summary').forEach((summary) =>
        safeAddEventListener(summary, 'click', this.onSummaryClick.bind(this))
      );
      this.querySelectorAll('button:not(.localization-selector)').forEach((button) =>
        safeAddEventListener(button, 'click', this.onCloseButtonClick.bind(this))
      );
    } catch (e) {
      console.warn('[MenuDrawer.bindEvents] failed', e);
    }
  }

  onKeyUp(event) {
    if (safeUpperCode(event) !== 'ESCAPE') return;

    const openDetailsElement = safeClosest(event && event.target, 'details[open]');
    if (!openDetailsElement) return;

    if (!this.mainDetailsToggle) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event && event.currentTarget;
    const detailsElement = summaryElement && summaryElement.parentNode;
    if (!summaryElement || !detailsElement) return;

    const parentMenuElement = detailsElement.closest ? detailsElement.closest('.has-submenu') : null;
    const isOpen = detailsElement.hasAttribute ? detailsElement.hasAttribute('open') : false;
    const reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

    const addTrapFocus = () => {
      try {
        trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
        summaryElement.nextElementSibling &&
          summaryElement.nextElementSibling.removeEventListener &&
          summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
      } catch (e) {
        console.warn('[MenuDrawer] addTrapFocus failed', e);
      }
    };

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen && event && event.preventDefault) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      try {
        if (window.matchMedia && window.matchMedia('(max-width: 990px)')) {
          document.documentElement &&
            document.documentElement.style &&
            document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
        }
      } catch (e) {
        // ignore
      }
    } else {
      setTimeout(() => {
        try {
          detailsElement.classList && detailsElement.classList.add('menu-opening');
          safeSetAttr(summaryElement, 'aria-expanded', 'true');
          parentMenuElement && parentMenuElement.classList && parentMenuElement.classList.add('submenu-open');

          if (!reducedMotion || reducedMotion.matches) {
            addTrapFocus();
          } else {
            summaryElement.nextElementSibling &&
              safeAddEventListener(summaryElement.nextElementSibling, 'transitionend', addTrapFocus);
          }
        } catch (e) {
          console.warn('[MenuDrawer] submenu open failed', e);
        }
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    if (!this.mainDetailsToggle) return;

    setTimeout(() => {
      this.mainDetailsToggle.classList && this.mainDetailsToggle.classList.add('menu-opening');
    });
    safeSetAttr(summaryElement, 'aria-expanded', 'true');
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body && document.body.classList && document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;
    if (!this.mainDetailsToggle) return;

    try {
      this.mainDetailsToggle.classList && this.mainDetailsToggle.classList.remove('menu-opening');
      this.mainDetailsToggle.querySelectorAll &&
        this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
          details.removeAttribute && details.removeAttribute('open');
          details.classList && details.classList.remove('menu-opening');
        });
      this.mainDetailsToggle.querySelectorAll &&
        this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
          submenu.classList && submenu.classList.remove('submenu-open');
        });

      document.body &&
        document.body.classList &&
        document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);

      removeTrapFocus(elementToFocus);
      this.closeAnimation(this.mainDetailsToggle);

      if (typeof KeyboardEvent !== 'undefined' && event instanceof KeyboardEvent) {
        elementToFocus && elementToFocus.setAttribute && elementToFocus.setAttribute('aria-expanded', 'false');
      }
    } catch (e) {
      console.warn('[MenuDrawer.closeMenuDrawer] failed', e);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      try {
        if (this.mainDetailsToggle && this.mainDetailsToggle.hasAttribute('open')) {
          if (!this.mainDetailsToggle.contains(document.activeElement)) this.closeMenuDrawer();
        }
      } catch (e) {
        // ignore
      }
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = safeClosest(event && event.currentTarget, 'details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    if (!detailsElement) return;

    try {
      const parentMenuElement = detailsElement.closest ? detailsElement.closest('.submenu-open') : null;
      parentMenuElement && parentMenuElement.classList && parentMenuElement.classList.remove('submenu-open');

      detailsElement.classList && detailsElement.classList.remove('menu-opening');

      const s = detailsElement.querySelector ? detailsElement.querySelector('summary') : null;
      s && safeSetAttr(s, 'aria-expanded', 'false');

      removeTrapFocus(s);
      this.closeAnimation(detailsElement);
    } catch (e) {
      console.warn('[MenuDrawer.closeSubmenu] failed', e);
    }
  }

  closeAnimation(detailsElement) {
    if (!detailsElement) return;

    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) animationStart = time;
      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        try {
          detailsElement.removeAttribute && detailsElement.removeAttribute('open');
          const openParent = detailsElement.closest ? detailsElement.closest('details[open]') : null;
          if (openParent) {
            trapFocus(openParent, detailsElement.querySelector && detailsElement.querySelector('summary'));
          }
        } catch (e) {
          console.warn('[MenuDrawer.closeAnimation] failed', e);
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

try {
  if (window.customElements && !customElements.get('menu-drawer')) {
    customElements.define('menu-drawer', MenuDrawer);
  }
} catch (e) {
  console.error('[customElements] define menu-drawer failed', e);
}

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    try {
      this.header = this.header || document.querySelector('.section-header');
      if (!this.header) return;

      const wrapper = this.closest ? this.closest('.header-wrapper') : null;
      const hasBorder = wrapper && wrapper.classList ? wrapper.classList.contains('header-wrapper--border-bottom') : false;
      this.borderOffset = this.borderOffset || hasBorder ? 1 : 0;

      document.documentElement &&
        document.documentElement.style &&
        document.documentElement.style.setProperty(
          '--header-bottom-position',
          `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset, 10)}px`
        );

      this.header.classList && this.header.classList.add('menu-open');

      setTimeout(() => {
        this.mainDetailsToggle && this.mainDetailsToggle.classList && this.mainDetailsToggle.classList.add('menu-opening');
      });

      safeSetAttr(summaryElement, 'aria-expanded', 'true');
      safeAddEventListener(window, 'resize', this.onResize);
      trapFocus(this.mainDetailsToggle, summaryElement);
      document.body && document.body.classList && document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    } catch (e) {
      console.warn('[HeaderDrawer.openMenuDrawer] failed', e);
    }
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    try {
      this.header && this.header.classList && this.header.classList.remove('menu-open');
      safeRemoveEventListener(window, 'resize', this.onResize);
    } catch (e) {
      // ignore
    }
  }

  onResize = () => {
    try {
      this.header &&
        document.documentElement &&
        document.documentElement.style &&
        document.documentElement.style.setProperty(
          '--header-bottom-position',
          `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset, 10)}px`
        );
      document.documentElement &&
        document.documentElement.style &&
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    } catch (e) {
      // ignore
    }
  };
}

try {
  if (window.customElements && !customElements.get('header-drawer')) {
    customElements.define('header-drawer', HeaderDrawer);
  }
} catch (e) {
  console.error('[customElements] define header-drawer failed', e);
}

class ModalDialog extends HTMLElement {
  constructor() {
    super();

    const closeBtn = this.querySelector ? this.querySelector('[id^="ModalClose-"]') : null;
    if (closeBtn) safeAddEventListener(closeBtn, 'click', this.hide.bind(this, false));

    safeAddEventListener(this, 'keyup', (event) => {
      if (safeUpperCode(event) === 'ESCAPE') this.hide();
    });

    if (this.classList && this.classList.contains('media-modal')) {
      safeAddEventListener(this, 'pointerup', (event) => {
        try {
          if (event.pointerType === 'mouse' && !(event.target && event.target.closest && event.target.closest('deferred-media, product-model'))) {
            this.hide();
          }
        } catch (e) {
          // ignore
        }
      });
    } else {
      safeAddEventListener(this, 'click', (event) => {
        if (event && event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    try {
      if (this.moved) return;
      this.moved = true;
      document.body && document.body.appendChild && document.body.appendChild(this);
    } catch (e) {
      console.warn('[ModalDialog.connectedCallback] failed', e);
    }
  }

  show(opener) {
    try {
      this.openedBy = opener;
      const popup = this.querySelector ? this.querySelector('.template-popup') : null;
      document.body && document.body.classList && document.body.classList.add('overflow-hidden');
      this.setAttribute && this.setAttribute('open', '');
      if (popup && popup.loadContent) popup.loadContent();
      trapFocus(this, this.querySelector ? this.querySelector('[role="dialog"]') : null);
      window.pauseAllMedia && window.pauseAllMedia();
    } catch (e) {
      console.warn('[ModalDialog.show] failed', e);
    }
  }

  hide() {
    try {
      document.body && document.body.classList && document.body.classList.remove('overflow-hidden');
      document.body && document.body.dispatchEvent && document.body.dispatchEvent(new CustomEvent('modalClosed'));
      this.removeAttribute && this.removeAttribute('open');
      removeTrapFocus(this.openedBy);
      window.pauseAllMedia && window.pauseAllMedia();
    } catch (e) {
      console.warn('[ModalDialog.hide] failed', e);
    }
  }
}

try {
  if (window.customElements && !customElements.get('modal-dialog')) {
    customElements.define('modal-dialog', ModalDialog);
  }
} catch (e) {
  console.error('[customElements] define modal-dialog failed', e);
}

class ModalOpener extends HTMLElement {
  constructor() {
    super();
    const button = this.querySelector ? this.querySelector('button') : null;
    if (!button) return;

    safeAddEventListener(button, 'click', () => {
      try {
        const selector = this.getAttribute && this.getAttribute('data-modal');
        if (!selector) return;
        const modal = document.querySelector(selector);
        if (modal && modal.show) modal.show(button);
      } catch (e) {
        console.warn('[ModalOpener] click failed', e);
      }
    });
  }
}

try {
  if (window.customElements && !customElements.get('modal-opener')) {
    customElements.define('modal-opener', ModalOpener);
  }
} catch (e) {
  console.error('[customElements] define modal-opener failed', e);
}

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector ? this.querySelector('[id^="Deferred-Poster-"]') : null;
    if (!poster) return;
    safeAddEventListener(poster, 'click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    try {
      window.pauseAllMedia && window.pauseAllMedia();

      if (this.getAttribute && !this.getAttribute('loaded')) {
        const tpl = this.querySelector ? this.querySelector('template') : null;
        if (!tpl || !tpl.content || !tpl.content.firstElementChild) return;

        const content = document.createElement('div');
        content.appendChild(tpl.content.firstElementChild.cloneNode(true));

        this.setAttribute && this.setAttribute('loaded', true);

        const media = content.querySelector ? content.querySelector('video, model-viewer, iframe') : null;
        if (!media) return;

        const deferredElement = this.appendChild(media);
        if (focus) safeFocus(deferredElement);

        if (deferredElement.nodeName === 'VIDEO' && deferredElement.getAttribute && deferredElement.getAttribute('autoplay')) {
          deferredElement.play && deferredElement.play();
        }
      }
    } catch (e) {
      console.warn('[DeferredMedia.loadContent] failed', e);
    }
  }
}

try {
  if (window.customElements && !customElements.get('deferred-media')) {
    customElements.define('deferred-media', DeferredMedia);
  }
} catch (e) {
  console.error('[customElements] define deferred-media failed', e);
}

/* =========================
 * Slider / Slideshow (minimal guards)
 * ========================= */
class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector ? this.querySelector('[id^="Slider-"]') : null;
    this.sliderItems = this.querySelectorAll ? this.querySelectorAll('[id^="Slide-"]') : [];
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector ? this.querySelector('.slider-counter--current') : null;
    this.pageTotalElement = this.querySelector ? this.querySelector('.slider-counter--total') : null;
    this.prevButton = this.querySelector ? this.querySelector('button[name="previous"]') : null;
    this.nextButton = this.querySelector ? this.querySelector('button[name="next"]') : null;

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    try {
      const resizeObserver = new ResizeObserver(() => this.initPages());
      resizeObserver.observe(this.slider);
    } catch (e) {
      // ResizeObserver 非対応でも落とさない
    }

    safeAddEventListener(this.slider, 'scroll', this.update.bind(this));
    if (this.prevButton) safeAddEventListener(this.prevButton, 'click', this.onButtonClick.bind(this));
    safeAddEventListener(this.nextButton, 'click', this.onButtonClick.bind(this));
  }

  initPages() {
    try {
      this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element && element.clientWidth > 0);
      if (this.sliderItemsToShow.length < 2) return;

      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
      if (!this.sliderItemOffset) return;

      this.slidesPerPage = Math.floor(
        (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
      );
      this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
      this.update();
    } catch (e) {
      console.warn('[SliderComponent.initPages] failed', e);
    }
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll ? this.querySelectorAll('[id^="Slide-"]') : [];
    this.initPages();
  }

  update() {
    if (!this.slider || !this.nextButton || !this.sliderItemOffset) return;

    try {
      const previousPage = this.currentPage;
      this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

      if (this.currentPageElement && this.pageTotalElement) {
        this.currentPageElement.textContent = this.currentPage;
        this.pageTotalElement.textContent = this.totalPages;
      }

      if (this.currentPage != previousPage) {
        this.dispatchEvent(
          new CustomEvent('slideChanged', {
            detail: {
              currentPage: this.currentPage,
              currentElement: this.sliderItemsToShow && this.sliderItemsToShow[this.currentPage - 1],
            },
          })
        );
      }

      if (this.enableSliderLooping) return;

      if (this.prevButton) {
        if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
          this.prevButton.setAttribute('disabled', 'disabled');
        } else {
          this.prevButton.removeAttribute('disabled');
        }
      }

      if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
        this.nextButton.setAttribute('disabled', 'disabled');
      } else {
        this.nextButton.removeAttribute('disabled');
      }
    } catch (e) {
      console.warn('[SliderComponent.update] failed', e);
    }
  }

  isSlideVisible(element, offset = 0) {
    if (!element || !this.slider) return false;
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event && event.preventDefault && event.preventDefault();
    if (!this.slider || !this.sliderItemOffset) return;

    const step = (event && event.currentTarget && event.currentTarget.dataset && event.currentTarget.dataset.step) || 1;
    const stepNum = parseInt(step, 10) || 1;

    this.slideScrollPosition =
      event.currentTarget && event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + stepNum * this.sliderItemOffset
        : this.slider.scrollLeft - stepNum * this.sliderItemOffset;

    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    try {
      this.slider && this.slider.scrollTo && this.slider.scrollTo({ left: position });
    } catch (e) {
      // ignore
    }
  }
}

try {
  if (window.customElements && !customElements.get('slider-component')) {
    customElements.define('slider-component', SliderComponent);
  }
} catch (e) {
  console.error('[customElements] define slider-component failed', e);
}

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector ? this.querySelector('.slider-buttons') : null;
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper || !this.slider) return;

    this.sliderFirstItemNode = this.slider.querySelector ? this.slider.querySelector('.slideshow__slide') : null;
    if (this.sliderItemsToShow && this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector ? this.querySelector('.announcement-bar-slider') : null;
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll ? this.sliderControlWrapper.querySelectorAll('.slider-counter__link') : []
    );
    this.sliderControlLinksArray.forEach((link) => safeAddEventListener(link, 'click', this.linkToSlide.bind(this)));

    safeAddEventListener(this.slider, 'scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
      if (this.reducedMotion && this.reducedMotion.addEventListener) {
        this.reducedMotion.addEventListener('change', () => {
          if (this.slider && this.slider.getAttribute && this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
        });
      }

      [this.prevButton, this.nextButton].forEach((button) => {
        if (!button) return;
        safeAddEventListener(
          button,
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute && this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    try {
      this.autoplaySpeed = (parseInt(this.slider.dataset.speed, 10) || 0) * 1000;
      if (!this.autoplaySpeed) return;

      safeAddEventListener(this, 'mouseover', this.focusInHandling.bind(this));
      safeAddEventListener(this, 'mouseleave', this.focusOutHandling.bind(this));
      safeAddEventListener(this, 'focusin', this.focusInHandling.bind(this));
      safeAddEventListener(this, 'focusout', this.focusOutHandling.bind(this));

      const btn = this.querySelector ? this.querySelector('.slideshow__autoplay') : null;
      if (btn) {
        this.sliderAutoplayButton = btn;
        safeAddEventListener(this.sliderAutoplayButton, 'click', this.autoPlayToggle.bind(this));
        this.autoplayButtonIsSetToPlay = true;
        this.play();
      } else {
        (this.reducedMotion && this.reducedMotion.matches) || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
      }
    } catch (e) {
      console.warn('[SlideshowComponent.setAutoPlay] failed', e);
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    if (!this.sliderItemsToShow) return;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event && event.currentTarget && event.currentTarget.name);
      return;
    }

    if (!this.slider || !this.sliderFirstItemNode) return;

    if (isFirstSlide && event.currentTarget && event.currentTarget.name === 'previous') {
      this.slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);
    this.applyAnimationToAnnouncementBar(event && event.currentTarget && event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      try {
        this.slider && this.slider.scrollTo && this.slider.scrollTo({ left: position });
      } catch (e) {
        // ignore
      }
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    if (!this.prevButton) return;

    try {
      this.sliderControlButtons = this.querySelectorAll ? this.querySelectorAll('.slider-counter__link') : [];
      this.prevButton.removeAttribute('disabled');

      if (!this.sliderControlButtons.length) return;

      this.sliderControlButtons.forEach((link) => {
        link.classList && link.classList.remove('slider-counter__link--active');
        link.removeAttribute && link.removeAttribute('aria-current');
      });

      const active = this.sliderControlButtons[this.currentPage - 1];
      active && active.classList && active.classList.add('slider-counter__link--active');
      active && active.setAttribute && active.setAttribute('aria-current', true);
    } catch (e) {
      console.warn('[SlideshowComponent.update] failed', e);
    }
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    try {
      if (this.sliderAutoplayButton) {
        const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
        if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
        this.play();
      } else if (!(this.reducedMotion && this.reducedMotion.matches) && !this.announcementBarArrowButtonWasClicked) {
        this.play();
      }
    } catch (e) {
      // ignore
    }
  }

  focusInHandling(event) {
    try {
      if (this.sliderAutoplayButton) {
        const focusedOnAutoplayButton = event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
        if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) this.play();
        else if (this.autoplayButtonIsSetToPlay) this.pause();
      } else if (this.announcementBarSlider && this.announcementBarSlider.contains(event.target)) {
        this.pause();
      }
    } catch (e) {
      // ignore
    }
  }

  play() {
    try {
      this.slider && this.slider.setAttribute && this.slider.setAttribute('aria-live', 'off');
      clearInterval(this.autoplay);
      this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
    } catch (e) {
      console.warn('[SlideshowComponent.play] failed', e);
    }
  }

  pause() {
    try {
      this.slider && this.slider.setAttribute && this.slider.setAttribute('aria-live', 'polite');
      clearInterval(this.autoplay);
    } catch (e) {
      // ignore
    }
  }

  togglePlayButtonState(pauseAutoplay) {
    if (!this.sliderAutoplayButton) return;

    try {
      if (pauseAutoplay) {
        this.sliderAutoplayButton.classList && this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
        const label = window.accessibilityStrings && window.accessibilityStrings.playSlideshow;
        label && this.sliderAutoplayButton.setAttribute && this.sliderAutoplayButton.setAttribute('aria-label', label);
      } else {
        this.sliderAutoplayButton.classList && this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
        const label = window.accessibilityStrings && window.accessibilityStrings.pauseSlideshow;
        label && this.sliderAutoplayButton.setAttribute && this.sliderAutoplayButton.setAttribute('aria-label', label);
      }
    } catch (e) {
      // ignore
    }
  }

  autoRotateSlides() {
    try {
      const slideScrollPosition =
        this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

      this.setSlidePosition(slideScrollPosition);
      this.applyAnimationToAnnouncementBar();
    } catch (e) {
      // ignore
    }
  }

  setSlideVisibility() {
    if (!this.sliderItemsToShow) return;

    this.sliderItemsToShow.forEach((item, index) => {
      if (!item) return;
      const linkElements = item.querySelectorAll ? item.querySelectorAll('a') : [];

      if (index === this.currentPage - 1) {
        linkElements.forEach((a) => a && a.removeAttribute && a.removeAttribute('tabindex'));
        item.setAttribute && item.setAttribute('aria-hidden', 'false');
        item.removeAttribute && item.removeAttribute('tabindex');
      } else {
        linkElements.forEach((a) => a && a.setAttribute && a.setAttribute('tabindex', '-1'));
        item.setAttribute && item.setAttribute('aria-hidden', 'true');
        item.setAttribute && item.setAttribute('tabindex', '-1');
      }
    });

    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider || !this.sliderItems || !this.sliderItems.length) return;

    try {
      const itemsCount = this.sliderItems.length;
      const increment = button === 'next' ? 1 : -1;

      const currentIndex = this.currentPage - 1;
      let nextIndex = (currentIndex + increment) % itemsCount;
      nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

      const nextSlide = this.sliderItems[nextIndex];
      const currentSlide = this.sliderItems[currentIndex];
      if (!nextSlide || !currentSlide) return;

      const animationClassIn = 'announcement-bar-slider--fade-in';
      const animationClassOut = 'announcement-bar-slider--fade-out';

      const isFirstSlide = currentIndex === 0;
      const isLastSlide = currentIndex === itemsCount - 1;

      const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
      const direction = shouldMoveNext ? 'next' : 'previous';

      currentSlide.classList && currentSlide.classList.add(`${animationClassOut}-${direction}`);
      nextSlide.classList && nextSlide.classList.add(`${animationClassIn}-${direction}`);

      setTimeout(() => {
        currentSlide.classList && currentSlide.classList.remove(`${animationClassOut}-${direction}`);
        nextSlide.classList && nextSlide.classList.remove(`${animationClassIn}-${direction}`);
      }, this.announcerBarAnimationDelay * 2);
    } catch (e) {
      console.warn('[SlideshowComponent.applyAnimationToAnnouncementBar] failed', e);
    }
  }

  linkToSlide(event) {
    event && event.preventDefault && event.preventDefault();
    if (!this.slider || !this.sliderFirstItemNode) return;

    const idx = this.sliderControlLinksArray ? this.sliderControlLinksArray.indexOf(event.currentTarget) : -1;
    if (idx < 0) return;

    const slideScrollPosition = this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * (idx + 1 - this.currentPage);
    try {
      this.slider.scrollTo && this.slider.scrollTo({ left: slideScrollPosition });
    } catch (e) {
      // ignore
    }
  }
}

try {
  if (window.customElements && !customElements.get('slideshow-component')) {
    customElements.define('slideshow-component', SlideshowComponent);
  }
} catch (e) {
  console.error('[customElements] define slideshow-component failed', e);
}

/* =========================
 * VariantSelects (guards)
 * ========================= */
class VariantSelects extends HTMLElement {
  constructor() {
    super();
    safeAddEventListener(this, 'change', this.onVariantChange);
  }

  onVariantChange() {
    try {
      this.updateOptions();
      this.updateMasterId();
      this.toggleAddButton(true, '', false);
      this.updatePickupAvailability();
      this.removeErrorMessage();
      this.updateVariantStatuses();

      if (!this.currentVariant) {
        this.toggleAddButton(true, '', true);
        this.setUnavailable();
      } else {
        this.updateMedia();
        this.updateURL();
        this.updateVariantInput();
        this.renderProductInfo();
        this.updateShareUrl();
      }
    } catch (e) {
      console.error('[VariantSelects.onVariantChange] failed', e);
    }
  }

  updateOptions() {
    try {
      this.options = Array.from(this.querySelectorAll('select'), (select) => select.value);
    } catch (e) {
      this.options = [];
    }
  }

  updateMasterId() {
    try {
      const data = this.getVariantData();
      this.currentVariant = (data || []).find((variant) => {
        return !variant.options
          .map((option, index) => this.options[index] === option)
          .includes(false);
      });
    } catch (e) {
      console.warn('[VariantSelects.updateMasterId] failed', e);
      this.currentVariant = null;
    }
  }

  updateMedia() {
    try {
      if (!this.currentVariant || !this.currentVariant.featured_media) return;

      const mediaGalleries = document.querySelectorAll(`[id^="MediaGallery-${this.dataset.section}"]`);
      mediaGalleries.forEach((mediaGallery) => {
        if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
          mediaGallery.setActiveMedia(`${this.dataset.section}-${this.currentVariant.featured_media.id}`, true);
        }
      });

      const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
      if (!modalContent) return;

      const newMediaModal = modalContent.querySelector(`[data-media-id="${this.currentVariant.featured_media.id}"]`);
      if (newMediaModal) modalContent.prepend(newMediaModal);
    } catch (e) {
      console.warn('[VariantSelects.updateMedia] failed', e);
    }
  }

  updateURL() {
    try {
      if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
      if (!window.history || !window.history.replaceState) return;
      window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
    } catch (e) {
      console.warn('[VariantSelects.updateURL] failed', e);
    }
  }

  updateShareUrl() {
    try {
      const shareButton = document.getElementById(`Share-${this.dataset.section}`);
      if (!shareButton || !shareButton.updateUrl) return;
      if (!window.shopUrl) return;
      shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
    } catch (e) {
      console.warn('[VariantSelects.updateShareUrl] failed', e);
    }
  }

  updateVariantInput() {
    try {
      const productForms = document.querySelectorAll(
        `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
      );
      productForms.forEach((productForm) => {
        const input = productForm.querySelector ? productForm.querySelector('input[name="id"]') : null;
        if (!input) return;
        input.value = this.currentVariant.id;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    } catch (e) {
      console.warn('[VariantSelects.updateVariantInput] failed', e);
    }
  }

  updateVariantStatuses() {
    try {
      const checked = this.querySelector ? this.querySelector(':checked') : null;
      if (!checked) return;

      // variantData が未準備でも落とさない
      const data = this.variantData || this.getVariantData() || [];
      const selectedOptionOneVariants = data.filter((variant) => checked.value === variant.option1);

      const inputWrappers = this.querySelectorAll ? [...this.querySelectorAll('.product-form__input')] : [];
      inputWrappers.forEach((option, index) => {
        if (index === 0) return;

        const optionInputs = option.querySelectorAll ? [...option.querySelectorAll('input[type="radio"], option')] : [];
        const prevChecked = inputWrappers[index - 1].querySelector ? inputWrappers[index - 1].querySelector(':checked') : null;
        const previousOptionSelected = prevChecked ? prevChecked.value : null;
        if (!previousOptionSelected) return;

        const availableOptionInputsValue = selectedOptionOneVariants
          .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
          .map((variantOption) => variantOption[`option${index + 1}`]);

        this.setInputAvailability(optionInputs, availableOptionInputsValue);
      });
    } catch (e) {
      console.warn('[VariantSelects.updateVariantStatuses] failed', e);
    }
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    try {
      listOfOptions.forEach((input) => {
        const val = input && input.getAttribute ? input.getAttribute('value') : null;
        if (!val) return;

        if (listOfAvailableOptions.includes(val)) {
          input.innerText = val;
        } else {
          const tmpl = window.variantStrings && window.variantStrings.unavailable_with_option;
          input.innerText = tmpl ? tmpl.replace('[value]', val) : val;
        }
      });
    } catch (e) {
      console.warn('[VariantSelects.setInputAvailability] failed', e);
    }
  }

  updatePickupAvailability() {
    try {
      const pickUpAvailability = document.querySelector('pickup-availability');
      if (!pickUpAvailability) return;

      if (this.currentVariant && this.currentVariant.available) {
        pickUpAvailability.fetchAvailability && pickUpAvailability.fetchAvailability(this.currentVariant.id);
      } else {
        pickUpAvailability.removeAttribute && pickUpAvailability.removeAttribute('available');
        pickUpAvailability.innerHTML = '';
      }
    } catch (e) {
      console.warn('[VariantSelects.updatePickupAvailability] failed', e);
    }
  }

  removeErrorMessage() {
    try {
      const section = this.closest ? this.closest('section') : null;
      if (!section) return;

      const productForm = section.querySelector ? section.querySelector('product-form') : null;
      if (productForm && productForm.handleErrorMessage) productForm.handleErrorMessage();
    } catch (e) {
      // ignore
    }
  }

  renderProductInfo() {
    try {
      if (!this.currentVariant || !this.currentVariant.id) return;

      const requestedVariantId = this.currentVariant.id;
      const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;

      const url = `${this.dataset.url}?variant=${requestedVariantId}&section_id=${sectionId}`;
      fetch(url)
        .then((response) => response.text())
        .then((responseText) => {
          try {
            // prevent unnecessary ui changes from abandoned selections
            if (!this.currentVariant || this.currentVariant.id !== requestedVariantId) return;

            const html = new DOMParser().parseFromString(responseText, 'text/html');

            const destination = document.getElementById(`price-${this.dataset.section}`);
            const source = html.getElementById(`price-${sectionId}`);

            const skuSource = html.getElementById(`Sku-${sectionId}`);
            const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);

            const inventorySource = html.getElementById(`Inventory-${sectionId}`);
            const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

            const volumePricingSource = html.getElementById(`Volume-${sectionId}`);

            const pricePerItemDestination = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
            const pricePerItemSource = html.getElementById(`Price-Per-Item-${sectionId}`);

            const volumePricingDestination = document.getElementById(`Volume-${this.dataset.section}`);
            const qtyRules = document.getElementById(`Quantity-Rules-${this.dataset.section}`);
            const volumeNote = document.getElementById(`Volume-Note-${this.dataset.section}`);

            if (volumeNote) volumeNote.classList && volumeNote.classList.remove('hidden');
            if (volumePricingDestination) volumePricingDestination.classList && volumePricingDestination.classList.remove('hidden');
            if (qtyRules) qtyRules.classList && qtyRules.classList.remove('hidden');

            if (source && destination) destination.innerHTML = source.innerHTML;
            if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;

            if (skuSource && skuDestination) {
              skuDestination.innerHTML = skuSource.innerHTML;
              skuDestination.classList && skuDestination.classList.toggle('hidden', skuSource.classList && skuSource.classList.contains('hidden'));
            }

            if (volumePricingSource && volumePricingDestination) {
              volumePricingDestination.innerHTML = volumePricingSource.innerHTML;
            }

            if (pricePerItemSource && pricePerItemDestination) {
              pricePerItemDestination.innerHTML = pricePerItemSource.innerHTML;
              pricePerItemDestination.classList &&
                pricePerItemDestination.classList.toggle('hidden', pricePerItemSource.classList && pricePerItemSource.classList.contains('hidden'));
            }

            const price = document.getElementById(`price-${this.dataset.section}`);
            if (price) price.classList && price.classList.remove('hidden');

            if (inventoryDestination && inventorySource) {
              inventoryDestination.classList &&
                inventoryDestination.classList.toggle('hidden', (inventorySource.innerText || '') === '');
            }

            const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);
            const soldOut = window.variantStrings && window.variantStrings.soldOut;
            this.toggleAddButton(addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true, soldOut);

            // PUB_SUB_EVENTS が無い/variantChange が無い場合でも落とさない
            const evtName = (window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.variantChange) || 'variantChange';
              publish(evtName, {
                sectionId,
                html,
                variant: this.currentVariant,
              });

          } catch (e) {
            console.warn('[VariantSelects.renderProductInfo] apply response failed', e);
          }
        })
        .catch((e) => console.error('[VariantSelects.renderProductInfo] fetch failed', e));
    } catch (e) {
      console.warn('[VariantSelects.renderProductInfo] failed', e);
    }
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    try {
      const productForm = document.getElementById(`product-form-${this.dataset.section}`);
      if (!productForm) return;

      const addButton = productForm.querySelector ? productForm.querySelector('[name="add"]') : null;
      const addButtonText = productForm.querySelector ? productForm.querySelector('[name="add"] > span') : null;
      if (!addButton) return;

      if (disable) {
        addButton.setAttribute && addButton.setAttribute('disabled', 'disabled');
        if (text && addButtonText) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute && addButton.removeAttribute('disabled');
        const t = window.variantStrings && window.variantStrings.addToCart;
        if (addButtonText) addButtonText.textContent = t || 'Add to cart';
      }

      if (!modifyClass) return;
      // 元コードの末尾が未実装なので、ここは何もしない（落とさないため）
    } catch (e) {
      console.warn('[VariantSelects.toggleAddButton] failed', e);
    }
  }

  setUnavailable() {
    try {
      const button = document.getElementById(`product-form-${this.dataset.section}`);
      if (!button) return;

      const addButton = button.querySelector ? button.querySelector('[name="add"]') : null;
      const addButtonText = button.querySelector ? button.querySelector('[name="add"] > span') : null;

      const price = document.getElementById(`price-${this.dataset.section}`);
      const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
      const sku = document.getElementById(`Sku-${this.dataset.section}`);
      const pricePerItem = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
      const volumeNote = document.getElementById(`Volume-Note-${this.dataset.section}`);
      const volumeTable = document.getElementById(`Volume-${this.dataset.section}`);
      const qtyRules = document.getElementById(`Quantity-Rules-${this.dataset.section}`);

      if (!addButton) return;

      const unavail = window.variantStrings && window.variantStrings.unavailable;
      if (addButtonText) addButtonText.textContent = unavail || 'Unavailable';

      price && price.classList && price.classList.add('hidden');
      inventory && inventory.classList && inventory.classList.add('hidden');
      sku && sku.classList && sku.classList.add('hidden');
      pricePerItem && pricePerItem.classList && pricePerItem.classList.add('hidden');
      volumeNote && volumeNote.classList && volumeNote.classList.add('hidden');
      volumeTable && volumeTable.classList && volumeTable.classList.add('hidden');
      qtyRules && qtyRules.classList && qtyRules.classList.add('hidden');
    } catch (e) {
      console.warn('[VariantSelects.setUnavailable] failed', e);
    }
  }

  getVariantData() {
    try {
      if (this.variantData) return this.variantData;
      const jsonEl = this.querySelector ? this.querySelector('[type="application/json"]') : null;
      const txt = jsonEl ? jsonEl.textContent : null;
      this.variantData = txt ? JSON.parse(txt) : [];
      return this.variantData;
    } catch (e) {
      console.warn('[VariantSelects.getVariantData] JSON parse failed', e);
      this.variantData = [];
      return this.variantData;
    }
  }
}

try {
  if (window.customElements && !customElements.get('variant-selects')) {
    customElements.define('variant-selects', VariantSelects);
  }
} catch (e) {
  console.error('[customElements] define variant-selects failed', e);
}

class VariantRadios extends VariantSelects {
  constructor() {
    super();
  }

  setInputAvailability(listOfOptions, listOfAvailableOptions) {
    try {
      listOfOptions.forEach((input) => {
        const val = input && input.getAttribute ? input.getAttribute('value') : null;
        if (!val) return;
        if (listOfAvailableOptions.includes(val)) {
          input.classList && input.classList.remove('disabled');
        } else {
          input.classList && input.classList.add('disabled');
        }
      });
    } catch (e) {
      console.warn('[VariantRadios.setInputAvailability] failed', e);
    }
  }

  updateOptions() {
    try {
      const fieldsets = Array.from(this.querySelectorAll('fieldset'));
      this.options = fieldsets.map((fieldset) => {
        const inputs = fieldset.querySelectorAll ? Array.from(fieldset.querySelectorAll('input')) : [];
        const checked = inputs.find((radio) => radio.checked);
        return checked ? checked.value : null;
      });
    } catch (e) {
      this.options = [];
    }
  }
}

try {
  if (window.customElements && !customElements.get('variant-radios')) {
    customElements.define('variant-radios', VariantRadios);
  }
} catch (e) {
  console.error('[customElements] define variant-radios failed', e);
}

/* =========================
 * ProductRecommendations (guards)
 * ========================= */
class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      try {
        if (!entries || !entries[0] || !entries[0].isIntersecting) return;
        observer && observer.unobserve && observer.unobserve(this);

        const url = this.dataset && this.dataset.url;
        if (!url) return;

        fetch(url)
          .then((response) => response.text())
          .then((text) => {
            try {
              const html = document.createElement('div');
              html.innerHTML = text;
              const recommendations = html.querySelector('product-recommendations');

              if (recommendations && recommendations.innerHTML.trim().length) {
                this.innerHTML = recommendations.innerHTML;
              }

              if (!this.querySelector('slideshow-component') && this.classList && this.classList.contains('complementary-products')) {
                this.remove();
              }

              if (html.querySelector('.grid__item')) {
                this.classList && this.classList.add('product-recommendations--loaded');
              }
            } catch (e) {
              console.warn('[ProductRecommendations] apply html failed', e);
            }
          })
          .catch((e) => console.error('[ProductRecommendations] fetch failed', e));
      } catch (e) {
        console.warn('[ProductRecommendations] intersection handler failed', e);
      }
    };

    try {
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(handleIntersection.bind(this), { rootMargin: '0px 0px 400px 0px' }).observe(this);
      } else {
        // フォールバック（古い環境）：即ロード
        handleIntersection([{ isIntersecting: true }], { unobserve: () => {} });
      }
    } catch (e) {
      console.warn('[ProductRecommendations.connectedCallback] failed', e);
    }
  }
}

try {
  if (window.customElements && !customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }
} catch (e) {
  console.error('[customElements] define product-recommendations failed', e);
}